import { HsaEligibility } from "@redotech/redo-conversion-hsa-common/hsa-eligibility";
import {
  GALE_PAYMENTS_ELIGIBILITY_METAFIELD_KEY,
  GALE_PAYMENTS_METAFIELD_NAMESPACE,
  GALE_PAYMENTS_PRODUCT_TYPE_METAFIELD_KEY,
  REDO_HSA_ELIGIBILITY_METAFIELD_KEY,
  REDO_HSA_METAFIELD_NAMESPACE,
  resolveHsaEligibilityFromMetafields,
} from "@redotech/redo-conversion-hsa-common/hsa-metafields";

/** The metafields the cart widget reads, in the Storefront API's identifier
 * shape. Declared here so the resolver and its caller cannot drift. */
export const HSA_METAFIELD_IDENTIFIERS = [
  {
    namespace: REDO_HSA_METAFIELD_NAMESPACE,
    key: REDO_HSA_ELIGIBILITY_METAFIELD_KEY,
  },
  {
    namespace: GALE_PAYMENTS_METAFIELD_NAMESPACE,
    key: GALE_PAYMENTS_ELIGIBILITY_METAFIELD_KEY,
  },
  {
    namespace: GALE_PAYMENTS_METAFIELD_NAMESPACE,
    key: GALE_PAYMENTS_PRODUCT_TYPE_METAFIELD_KEY,
  },
];

export interface HsaCartEligibilitySummary {
  /** Cart lines resolving eligible or eligible-with-LMN. */
  eligibleLineCount: number;
  /** Total quantity across eligible lines. */
  eligibleQuantity: number;
  /** Total lines in the cart. */
  cartLineCount: number;
  /** True when every cart line is eligible (directly or with LMN). */
  fullyEligible: boolean;
  /** True when at least one eligible line requires a Letter of Medical
   * Necessity, so the widget's copy must scope eligibility to qualified
   * customers and the walkthrough must show its health-assessment step. */
  anyRequiresLmn: boolean;
  /** True when any line, eligible or not, bills again: Shopify then runs a
   * subscription checkout for the whole cart. */
  hasSubscription: boolean;
}

/** The cart facts the summary depends on; a narrowing of `ShopifyCartItem`. */
export interface HsaCartLine {
  variantId: string;
  quantity: number;
  recurring: boolean;
}

/** The cart-line field subscription detection reads; `ShopifyCartItem`
 * satisfies it. */
export interface HsaCartItemSellingPlan {
  selling_plan_allocation?: {
    selling_plan: { recurring_deliveries: boolean };
  } | null;
}

/** Never widen to "has a selling plan": Try First attaches pre-order and
 * try-before-you-buy plans, which do not recur. */
export function isRecurringCartItem(item: HsaCartItemSellingPlan): boolean {
  return (
    item.selling_plan_allocation?.selling_plan.recurring_deliveries ?? false
  );
}

/**
 * Resolves one variant's eligibility from the metafields the Storefront API
 * returned for it. An absent variant (Shopify omits unpublished products) and
 * an absent metafield both mean not eligible.
 */
export function eligibilityFromMetafields(
  metafields: { namespace: string; key: string; value: string }[] | undefined,
): HsaEligibility {
  const valueOf = (namespace: string, key: string) =>
    metafields?.find((mf) => mf.namespace === namespace && mf.key === key)
      ?.value ?? null;
  return resolveHsaEligibilityFromMetafields({
    redoValue: valueOf(
      REDO_HSA_METAFIELD_NAMESPACE,
      REDO_HSA_ELIGIBILITY_METAFIELD_KEY,
    ),
    galeEligibilityValue: valueOf(
      GALE_PAYMENTS_METAFIELD_NAMESPACE,
      GALE_PAYMENTS_ELIGIBILITY_METAFIELD_KEY,
    ),
    galeProductTypeValue: valueOf(
      GALE_PAYMENTS_METAFIELD_NAMESPACE,
      GALE_PAYMENTS_PRODUCT_TYPE_METAFIELD_KEY,
    ),
  });
}

/**
 * Aggregates cart lines against resolved per-variant eligibility. A variant
 * missing from the map counts as not eligible, so a failed or partial lookup
 * under-claims rather than over-claims.
 */
export function summarizeHsaCart(
  lines: HsaCartLine[],
  eligibilityByVariantId: ReadonlyMap<string, HsaEligibility>,
): HsaCartEligibilitySummary {
  let eligibleLineCount = 0;
  let eligibleQuantity = 0;
  let anyRequiresLmn = false;
  let hasSubscription = false;
  for (const line of lines) {
    if (line.recurring) {
      hasSubscription = true;
    }
    const eligibility =
      eligibilityByVariantId.get(line.variantId) ?? HsaEligibility.NOT_ELIGIBLE;
    if (eligibility === HsaEligibility.NOT_ELIGIBLE) {
      continue;
    }
    eligibleLineCount += 1;
    eligibleQuantity += line.quantity;
    if (eligibility === HsaEligibility.ELIGIBLE_WITH_LMN) {
      anyRequiresLmn = true;
    }
  }
  return {
    eligibleLineCount,
    eligibleQuantity,
    cartLineCount: lines.length,
    // An empty cart is not "fully eligible" — 0 === 0 would otherwise claim it.
    fullyEligible: lines.length > 0 && eligibleLineCount === lines.length,
    anyRequiresLmn,
    hasSubscription,
  };
}
